import React from 'react';
import {
  Box,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { BackfillPreviewItem } from '../../types/contentCompletion';

export const isIngestiblePreviewItem = (item: BackfillPreviewItem): boolean =>
  Boolean(item.url?.trim() && (item.title?.trim() || item.published_date));

export const defaultSelectedUrls = (items: BackfillPreviewItem[]): Set<string> =>
  new Set(
    items
      .filter(isIngestiblePreviewItem)
      .map((item) => item.url as string)
  );

interface BackfillReviewTableProps {
  items: BackfillPreviewItem[];
  selectedUrls: Set<string>;
  onToggleUrl: (url: string) => void;
  onToggleSelectAll: () => void;
  disabled?: boolean;
  maxHeight?: number | string;
}

const BackfillReviewTable: React.FC<BackfillReviewTableProps> = ({
  items,
  selectedUrls,
  onToggleUrl,
  onToggleSelectAll,
  disabled = false,
  maxHeight = 280,
}) => {
  const selectableItems = items.filter(isIngestiblePreviewItem);
  const selectedCount = selectableItems.filter((item) =>
    selectedUrls.has(item.url as string)
  ).length;
  const allSelectableSelected =
    selectableItems.length > 0 && selectedCount === selectableItems.length;
  const someSelectableSelected =
    selectedCount > 0 && selectedCount < selectableItems.length;

  return (
    <Box sx={{ maxHeight, overflow: 'auto' }}>
      <Table size="small" stickyHeader>
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox">
            <Checkbox
              indeterminate={someSelectableSelected}
              checked={allSelectableSelected}
              onChange={onToggleSelectAll}
              disabled={disabled || selectableItems.length === 0}
              inputProps={{ 'aria-label': 'Select all press releases' }}
            />
          </TableCell>
          <TableCell>Title</TableCell>
          <TableCell>Date</TableCell>
          <TableCell>Content</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map((item, idx) => {
          const url = item.url || '';
          const canSelect = isIngestiblePreviewItem(item);
          const isSelected = canSelect && selectedUrls.has(url);

          return (
            <TableRow key={`${url}-${idx}`} selected={isSelected} hover={canSelect}>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={isSelected}
                  onChange={() => onToggleUrl(url)}
                  disabled={disabled || !canSelect}
                  inputProps={{
                    'aria-label': `Select ${item.title || url || 'press release'}`,
                  }}
                />
              </TableCell>
              <TableCell>
                {item.url ? (
                  <a href={item.url} target="_blank" rel="noopener noreferrer">
                    {item.title || item.url}
                  </a>
                ) : (
                  item.title || '—'
                )}
              </TableCell>
              <TableCell>{item.published_date || '—'}</TableCell>
              <TableCell>{item.has_full_text ? 'Yes' : 'Metadata only'}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
      </Table>
    </Box>
  );
};

export default BackfillReviewTable;
